import { formatPerComparableUnit } from '@domain/product'
import type { FamilyComparison } from '@domain/product'

/**
 * Todos os tamanhos de uma família numa escala só.
 *
 * ## Por que uma régua, e não uma lista de números
 *
 * "R$ 4,50/L" e "R$ 11,40/L" são comparáveis, mas comparar exige ler os dois e
 * fazer a conta de cabeça. A barra faz a conta: dá para ver qual ganha antes
 * de ler qualquer coisa. É o que tira a resposta de dentro do detalhe e a põe
 * na linha da lista.
 *
 * ## Comprimento é proporcional ao valor, e o vencedor é o MENOR
 *
 * A tentação é inverter — "quem ganha tem a barra maior" — e é mentira: barra
 * comprida tem que significar número grande, senão o gráfico contradiz o eixo.
 * Quem resolve a leitura é a cor (ciano no vencedor, o resto apagado) e o
 * multiplicador, que diz **quanto** pior é a segunda opção. Sem ele, duas
 * barras de comprimento parecido escondem uma diferença de 20%.
 */
export function ReguaDeFamilia({
  comparacao,
  className = '',
}: {
  comparacao: FamilyComparison
  className?: string
}) {
  const { ranked, incomparable, semPreco } = comparacao
  const melhor = ranked[0]
  const teto = ranked[ranked.length - 1]?.referenceMilliCents ?? 0
  /**
   * Com um tamanho só NÃO se desenha barra.
   *
   * A escala é por família, e uma barra sozinha ocupa 100% do próprio teto —
   * ou seja, não diz nada. Pior: ao lado de uma família onde o vencedor ficou
   * em 75%, a barra cheia do item único faz ele **parecer mais caro**, e a
   * pessoa compara duas escalas que não se falam. Visto em produção, com a
   * "Água com gás Grande" ao lado da "Água com gás".
   *
   * Sem comparação, o que sobra é o preço — e é o que a linha mostra.
   */
  const compara = ranked.length > 1

  // Nada com preço: a régua vira o convite pelo dado que falta. Um espaço em
  // branco diria que não há nada aqui; o tracejado diz que falta preencher.
  if (melhor === undefined) {
    return (
      <p className={`text-xs text-[var(--text-muted)] ${className}`.trim()}>
        <span className="mr-2 inline-block h-[6px] w-16 rounded-full border border-dashed border-[var(--border)] align-middle" />
        {incomparable.length > 0
          ? 'sem tamanho declarado'
          : semPreco.length > 0
            ? 'sem preço ainda'
            : 'sem tamanho nem preço'}
      </p>
    )
  }

  return (
    <ul className={`flex flex-col gap-1 ${className}`.trim()} aria-label="Preço por unidade">
      {ranked.map((v, i) => {
        const ganha = i === 0
        // Sobre o menor, não sobre o maior: "2,5× mais caro que o melhor" é a
        // frase que decide a compra.
        const vezes = v.referenceMilliCents / melhor.referenceMilliCents
        return (
          <li key={v.product.id} className="grid grid-cols-[1fr_auto] items-center gap-x-2 text-xs">
            <span className="col-span-2 flex items-baseline justify-between gap-2">
              <span className="min-w-0 truncate text-[var(--text-muted)]">{v.product.name}</span>
              {/* `shrink-0` e `whitespace-nowrap`: sem os dois, "R$ 49,48 / L"
                  quebrava entre o número e a unidade em 375px, e a coluna de
                  valores deixava de ser uma coluna. Quem cede espaço é o nome,
                  que já trunca — o preço é o que a linha existe para dizer. */}
              <span
                className="shrink-0 whitespace-nowrap tabular-nums"
                style={{ color: ganha ? 'var(--economia)' : 'var(--text-muted)' }}
              >
                {formatPerComparableUnit(v.referenceMilliCents, v.baseUnit)}
                {ganha ? null : (
                  <span className="ml-1 text-[var(--text-faint)]">
                    {vezes.toFixed(1).replace('.', ',')}×
                  </span>
                )}
              </span>
            </span>
            {compara && (
            <span
              className="col-span-2 h-[5px] rounded-full"
              style={{
                background: ganha ? 'var(--economia)' : 'rgb(255 255 255 / 0.16)',
                // Contra o teto, e não contra o melhor: assim a barra do
                // vencedor encolhe quando a família ganha um tamanho muito
                // pior, que é exatamente a informação nova.
                width: `${String(Math.max(6, (v.referenceMilliCents / teto) * 100))}%`,
              }}
            />
            )}
          </li>
        )
      })}

      {/* Os que não entram na conta aparecem, e dizem por quê. Escondê-los
          faria a família parecer menor do que é — e é justamente neles que
          falta o dado que o app precisa. */}
      {incomparable.length > 0 && (
        <li className="text-xs" style={{ color: 'var(--pendente)' }}>
          {incomparable.length === 1
            ? '1 sem tamanho declarado'
            : `${String(incomparable.length)} sem tamanho declarado`}
        </li>
      )}
      {semPreco.length > 0 && (
        <li className="text-xs text-[var(--text-faint)]">
          {semPreco.length === 1
            ? '1 sem preço ainda'
            : `${String(semPreco.length)} sem preço ainda`}
        </li>
      )}
    </ul>
  )
}
